import React, { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import CompanyTable from "./company/CompanyTable";
import { COLUMNS_CONSTANTS as COMPANY_COLUMNS } from "./company/CompanyTable";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import exportCsv from "bundles/common/utils/exportCsv";
import {
  EMPTY_COMPANY_FILTERS,
} from "bundles/Internal/components/company/CompanyTableFilters";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.countries?.length) {
    filters.country = activeFilters.countries;
    hasAny = true;
  }

  if (activeFilters.industries?.length) {
    filters.company_industry = activeFilters.industries;
    hasAny = true;
  }

  if (activeFilters.team_sizes?.length) {
    filters.team_size = activeFilters.team_sizes;
    hasAny = true;
  }

  if (activeFilters.cities?.length) {
    filters.city = activeFilters.cities;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const CompaniesIndexPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesCompany, setNotesCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_COMPANY_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [followedCompanyIds, setFollowedCompanyIds] = useState(() => new Set());

  useEffect(() => {
    sh.get("/internal/follows")
      .then((res) => setFollowedCompanyIds(new Set(res.data?.Company || [])))
      .catch(() => {});
  }, []);

  const handleToggleFollow = useCallback((companyId, next) => {
    setFollowedCompanyIds((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(companyId);
      else updated.delete(companyId);
      return updated;
    });
  }, []);

  const fetchCompanies = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/companies", {
      params: {
        query: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCompanies(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch companies", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchCompanies(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchCompanies, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchCompanies(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchCompanies, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback((ids) => {
    exportCsv("companies", ids, authenticity_token, COMPANY_COLUMNS)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [authenticity_token, enqueueSnackbar]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <CompanyTable
        companies={companies}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onCompanySelect={(company) => {
          if (!company?.id) return;
          navigate(`/internal/companies/${company.id}`, {
            state: { company, from: location.pathname },
          });
        }}
        onOpenNotes={(company) => {
          setNotesCompany(company);
          setNotesOpen(true);
        }}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        onExportCsv={handleExportCsv}
        followedCompanyIds={followedCompanyIds}
        onToggleFollow={handleToggleFollow}
      />
      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="companies"
        record={notesCompany}
        onNoteAdded={(companyId) => {
          setCompanies((prev) =>
            prev.map((c) =>
              String(c?.id) === String(companyId)
                ? { ...c, notes_count: (Number(c.notes_count) || 0) + 1 }
                : c
            )
          );
        }}
        onNoteArchived={(companyId) => {
          setCompanies((prev) =>
            prev.map((c) =>
              String(c?.id) === String(companyId)
                ? { ...c, notes_count: Math.max((Number(c.notes_count) || 0) - 1, 0) }
                : c
            )
          );
        }}
      />
    </Box>
  );
};

export default CompaniesIndexPage;
